<template>
  <div class="c-base-time-picker">
    <div v-if="date" class="c-base-time-picker__date">
      {{ $d(date, 'short') }}
    </div>

    <base-form-group :class="{ 'c-base-form__form-group--error': $v.form.$anyError }">
      <base-input-group class="c-base-time-picker__input-group">
        <font-awesome-icon class="mr-2" :icon="['fal', 'clock']" />
        <base-select class="c-base-time-picker__select" :options="hourOptions" v-model.number="$v.form.hours.$model" :state="getValidationState($v.form.hours)" size="sm"/>
        <span class="mx-1">:</span>
        <base-select class="c-base-time-picker__select" :options="minuteOptions" v-model.number="$v.form.minutes.$model" :is-taggable="true" :create-option="createOption" :state="getValidationState($v.form.minutes)" size="sm"/>
        <template v-if="canPickSeconds">
          <span class="mx-1">:</span>
          <base-select class="c-base-time-picker__select" :options="secondOptions" v-model.number="$v.form.seconds.$model" :is-taggable="true" :create-option="createOption" :state="getValidationState($v.form.seconds)" size="sm"/>
        </template>
      </base-input-group>

      <div class="c-base-form__error-message" v-if="!$v.form.hours.between">{{ $t('system.between', { label: $t('label.hours'), min: $v.form.hours.$params.between.min, max: $v.form.hours.$params.between.max }) }}</div>
      <div class="c-base-form__error-message" v-if="!$v.form.minutes.between">{{ $t('system.between', { label: $t('label.minutes'), min: $v.form.minutes.$params.between.min, max: $v.form.minutes.$params.between.max }) }}</div>
      <div class="c-base-form__error-message" v-if="!$v.form.seconds.between">{{ $t('system.between', { label: $t('label.seconds'), min: $v.form.seconds.$params.between.min, max: $v.form.seconds.$params.between.max }) }}</div>
    </base-form-group>
  </div>
</template>

<script>
/**
 * How to use the time picker
 *
 * As time picker:
 * <base-time-picker v-model="time" />
 *
 * As time picker with seconds
 * <base-time-picker v-model="time" :can-pick-seconds="canPickSeconds" />
 *
 * Properties:
 * - value { Object }: Selected date or date range
 * - date { Date } (Optional): The date to show with the time picker
 * - can-pick-seconds { Boolean }: Enable seconds option
 * - minute-increment { Number }: Increment amount for the minute select options
 * - second-increment { Number }: Increment amount for the second select options
 *
 * Default properties:
 * - value: { hours: 0, minutes: 0, seconds: 0 }
 * - can-pick-seconds: false
 * - minute-increment: 5
 * - second-increment: 1
 */

import { between } from 'vuelidate/lib/validators'
import { validationState } from '@/mixins/helpers'

export default {
  name: 'BaseTimePicker',

  mixins: [validationState],

  props: {
    value: {
      type: Object,
      default: () => ({ hours: 0, minutes: 0, seconds: 0 })
    },
    date: {
      type: Date
    },
    canPickSeconds: {
      type: Boolean,
      default: false
    },
    minuteIncrement: {
      type: Number,
      default: 5
    },
    secondIncrement: {
      type: Number,
      default: 1
    }
  },

  data () {
    return {
      form: {
        hours: 0,
        minutes: 0,
        seconds: 0
      }
    }
  },

  mounted () {
    this.$watch(() => this.value, ({ hours = 0, minutes = 0, seconds = 0 }) => {
      this.form = { hours, minutes, seconds }

      this.$nextTick(() => {
        this.$v.$reset()
      })
    }, { immediate: true })

    this.$watch(() => this.form.hours, this.updateValue)
    this.$watch(() => this.form.minutes, this.updateValue)
    this.$watch(() => this.form.seconds, this.updateValue)
  },

  computed: {
    /**
     * Generate a list of options per hour
     */
    hourOptions () {
      return Array.from({ length: 24 }, (value, index) => index).map(item => ({ value: item, label: `0${item}`.slice(-2) }))
    },

    /**
     * Generate a list of options per minute
     */
    minuteOptions () {
      return Array.from({ length: Math.ceil(60 / this.minuteIncrement) }, (value, index) => this.minuteIncrement * index).map(item => ({ value: item, label: `0${item}`.slice(-2) }))
    },

    /**
     * Generate a list of options per second
     */
    secondOptions () {
      return Array.from({ length: Math.ceil(60 / this.secondIncrement) }, (value, index) => this.secondIncrement * index).map(item => ({ value: item, label: `0${item}`.slice(-2) }))
    }
  },

  validations () {
    return {
      form: {
        hours: { between: between(0, 23) },
        minutes: { between: between(0, 59) },
        seconds: { between: between(0, 59) }
      }
    }
  },

  methods: {
    createOption (option) {
      return { value: option.replace(/\D/g, ''), label: `0${option.replace(/\D/g, '')}`.slice(-2) }
    },

    updateValue () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.$emit('input', { ...this.value, ...this.form })
      }
    }
  }
}
</script>
